import api from './api'

class JiraService {
  /**
   * Busca métricas de um usuário específico
   * @param {string} tenantId - ID do tenant
   * @param {string} userId - ID do usuário
   * @param {Object} params - Parâmetros opcionais (teamId, startDate, endDate)
   * @returns {Promise} Métricas do usuário
   */
  getUserMetrics(tenantId, userId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/users/${userId}/jira/metrics`, { params })
  }

  /**
   * Busca métricas de toda a equipe
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Array com métricas de cada usuário da equipe
   */
  getTeamMetrics(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/team`, { params })
  }

  /**
   * Busca resumo agregado das métricas
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Resumo com totais e médias
   */
  getSummary(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/summary`, { params })
  }

  // ============ MÉTRICAS ESTENDIDAS ============

  /**
   * Busca métricas estendidas de um usuário (inclui lead time, cycle time, worklogs)
   * @param {string} tenantId - ID do tenant
   * @param {string} userId - ID do usuário
   * @param {Object} params - Parâmetros opcionais (teamId, startDate, endDate)
   * @returns {Promise} Métricas estendidas
   */
  getExtendedMetrics(tenantId, userId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/users/${userId}/jira/metrics/extended`, { params })
  }

  /**
   * Busca worklogs de um usuário
   * @param {string} tenantId - ID do tenant
   * @param {string} userId - ID do usuário
   * @param {Object} params - Parâmetros opcionais (startDate, endDate)
   * @returns {Promise} Worklogs do usuário
   */
  getUserWorklogs(tenantId, userId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/users/${userId}/jira/worklogs`, { params })
  }

  /**
   * Busca issues por projeto
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Issues agrupadas por projeto
   */
  getIssuesByProject(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/projects`, { params })
  }

  /**
   * Busca lead time do tenant e por usuário
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Lead time médio e por usuário
   */
  getLeadTime(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/leadtime`, { params })
  }

  /**
   * Busca cycle time do tenant e por usuário
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Cycle time médio e por usuário
   */
  getCycleTime(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/cycletime`, { params })
  }

  /**
   * Busca velocity (story points) do tenant e por usuário
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Velocity da equipe e individual
   */
  getVelocity(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/velocity`, { params })
  }

  /**
   * Busca issues criadas
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Issues criadas por usuário
   */
  getIssuesCreated(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/created`, { params })
  }

  /**
   * Busca issues reabertas
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Issues reabertas por usuário
   */
  getIssuesReopened(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/reopened`, { params })
  }

  /**
   * Busca atividade de comentários
   * @param {string} tenantId - ID do tenant
   * @param {Object} params - Parâmetros opcionais (teamId, userId, startDate, endDate)
   * @returns {Promise} Comentários por usuário
   */
  getComments(tenantId, params = {}) {
    return api.get(`/api/tenant/${tenantId}/jira/metrics/comments`, { params })
  }
}

export default new JiraService()
